import {
  AlertOccurrencesModal,
  ContentDeciderContainer,
  Loader,
  withErrorBoundary
} from '@nile/components';
import {
  RMMClient,
  AccountClient,
  GET_PERSONALIZATION_BY_NAME
} from '@nile/graphql';
import { useQuery } from '@apollo/client';
import envConfig from '@nile/home/src/Config/env';
import { HomeContext } from '@nile/mangifera/src/Contexts';
import TicketContext from '@nile/mangifera/src/Contexts/TicketContext';
import { GET_ALERT_LIST } from '@nile/rmm/src/modules/alert-management/Graphql/alert';
import TableView from '@nile/banyan/src/Pages/ServiceCatalog/TableView';
import { isEditable } from '@nile/mangifera/src/Pebbles/Favourites';
import {
  getType as getAlertType,
  getAccessor as getAlertAccessor,
  columnsConfig as alertColumnsConfig,
  getHomeWidgetMeta
} from '@nile/rmm/src/modules/alert-management/services/alert-list-helper.service';
import { assetDetailsTab } from '@nile/rmm/src/modules/alert-management/constants/constants';
import { getTabTemplate } from '@nile/mangifera/src/Pebbles/TabManager';
import { cloneDeep, isEmpty } from 'lodash';
import { getProcessedConfig } from '@nile/rmm/src/modules/common/services/tableHelper';
import React, { useCallback, useContext, useState } from 'react';
import sizeMe from 'react-sizeme';
import { getEmoji } from '@nile/mangifera/src/Pebbles/WidgetEmoji';
import { Tag } from '@superops/ui-design-system';
import { SVGIcon } from '@superops/super-icons';
import { useAlertOccurrencesModal } from '@nile/rmm/src/modules/alert-management/hooks/useAlertOccurrencesModal';
import AccessDenied from './AccessDenied';
import DeletedView from './DeletedView';
import {
  WidgetWrapper,
  Header,
  Title,
  Content,
  Actions,
  NumberCardContainer,
  NumberTitle,
  Badge
} from '../HomeWidgetContainer';

function AlertViewWidget(props) {
  const {
    widget,
    user,
    meta,
    size,
    intl,
    alertViews,
    deleteWidget,
    alertViewMeta = {}
  } = props;

  const viewPermission = widget?.permission;
  const viewExist = alertViews.some((el) => el.id === widget.id);

  const context = useContext(HomeContext);
  const tabContext = useContext(TicketContext);
  const [loading, setLoading] = useState(!!widget && viewPermission);
  const [data, setData] = useState([]);
  const [listInfo, setListInfo] = useState({});
  const [columnGroup, setColumnGroup] = useState([
    { Header: 'Name', columns: [] }
  ]);

  // Alert Occurrences Modal hook
  const { handleViewOccurrences, modalProps } = useAlertOccurrencesModal({
    intl,
    showActionBtns: false
  });

  const { configuredCols } = getProcessedConfig({
    entitiesMeta: meta,
    allColumns: alertViewMeta?.selectAttributes || [],
    intl,
    module: 'Alert',
    columnPersonalization: [],
    searchAttributes: alertViewMeta?.searchAttributes || []
  });
  const columns = configuredCols;

  let currentView =
    viewPermission && viewExist
      ? alertViews.find((el) => el.id === widget.id)
      : widget;
  currentView = {
    ...currentView,
    module: 'ALERT'
  };
  const options = {
    pageSize: 20,
    page: 1,
    view: { id: widget.id },
    sort: [
      {
        attribute: 'createdTime',
        order: 'DESC'
      }
    ]
  };

  const navigateToView = () => {
    if (!viewPermission || !viewExist) return;
    const currentAppState = context?.userAppState || { appState: [] };
    const updatedAppState = cloneDeep(currentAppState.appState);
    const tabIdx = updatedAppState.findIndex((el) => el.id === 'rmm-home');
    const tab = {
      id: 'rmm-home',
      content: getTabTemplate({ module: 'RMM_HOME' }),
      accessibilityLabel: 'RMM Home',
      module: 'RMM_HOME',
      tabId: 1,
      state: {},
      data: {},
      url: `/rmm/alert/${currentView.id}`
    };
    if (tabIdx === -1) {
      updatedAppState.push(tab);
    } else {
      updatedAppState[tabIdx] = tab;
    }
    context.updateUserAppState({
      ...currentAppState,
      appState: updatedAppState,
      activeTabIndex: updatedAppState.findIndex((el) => el.id === tab.id)
    });
    context.closeHomeOverlay();
    setTimeout(() => {
      context.switchTabFocus(tab);
    }, 200);
  };

  const navToDetailsPage = (obj) => {
    const { assetId, name, assetClass } = obj?.row?.original?.asset;
    tabContext.openTab({
      module: assetDetailsTab,
      data: {
        id: assetId,
        subject: name,
        classId: assetClass?.classId
      }
    });
  };

  const getActions = useCallback((col) => {
    switch (col.name) {
      case 'asset.name':
        return {
          onClick: navToDetailsPage
        };
      case 'occurrenceCount':
        return {
          onViewOccurrences: handleViewOccurrences
        };
      default:
        return {};
    }
  });

  const fetchViewData = async (personalization, pageOptions) => {
    setLoading(true);
    const personalizedColumns = personalization
      ? JSON.parse(personalization)
      : columns;
    const response = await RMMClient.query({
      query: GET_ALERT_LIST,
      variables: {
        listInfo: pageOptions
          ? { ...pageOptions, view: { id: widget.id } }
          : options
      },
      fetchPolicy: 'network-only'
    });
    setLoading(false);
    const cols = personalizedColumns;

    const allColumns = cols.map((col) => {
      return {
        Header: col.label,
        accessor: getAlertAccessor(col),
        type: getAlertType(col),
        disableSortBy: true,
        disableFilters: true,
        className: 'sticky',
        meta: getHomeWidgetMeta(col),
        disableResizing: false,
        editable: isEditable(col),
        actions: getActions(col),
        field: col
      };
    });
    setColumnGroup([{ Header: 'Name', columns: allColumns }]);
    setData(response?.data?.getAlertList?.alerts || []);
    setListInfo(response?.data?.getAlertList?.listInfo);
  };

  useQuery(GET_PERSONALIZATION_BY_NAME, {
    variables: {
      userId: user.userId,
      category: 'AlertView',
      name: widget?.id
    },
    client: AccountClient,
    skip: !viewPermission,
    onCompleted(res) {
      setLoading(true);
      const personalizationJSON =
        res?.getPersonalizationByCategoryAndName?.value;
      fetchViewData(personalizationJSON);
    },
    onError(err) {
      setLoading(false);
      console.log(err);
    }
  });

  if (loading) {
    return (
      <WidgetWrapper>
        <div className="widget-header flex center fg1">
          <Actions className="widget-actions visibilityActions">
            <div onClick={deleteWidget.bind(this, widget)} className="ptr">
              <SVGIcon name="Delete" />
            </div>
          </Actions>
        </div>
        <Loader />
      </WidgetWrapper>
    );
  }
  return (
    <WidgetWrapper>
      {size.width > 300 && size.height > 200 ? (
        <>
          <Header>
            <Title onClick={navigateToView}>
              <Badge className="mr15" color="#f87b0533">
                {getEmoji(currentView)}
              </Badge>
              {currentView?.name}
              {viewPermission && viewExist && (
                <div className="ml-10">
                  <Tag>
                    {data?.length}/{listInfo?.totalCount}
                  </Tag>
                </div>
              )}
            </Title>
            <div className="widget-header flex center fg1">
              <Actions className="widget-actions visibilityActions">
                <div onClick={deleteWidget.bind(this, widget)} className="ptr">
                  <SVGIcon name="Delete" />
                </div>
              </Actions>
            </div>
          </Header>
          {!viewPermission ? (
            <AccessDenied />
          ) : (
            <>
              {!viewExist ? (
                <DeletedView />
              ) : (
                <Content>
                  <ContentDeciderContainer
                    isLoading={loading}
                    hasContent={!loading && data?.length > 0}
                    fullHeight
                    mainContent={
                      <TableView
                        fullHeight
                        noBulkSelect
                        context={context}
                        columns={columnGroup}
                        data={data}
                        setData={setData}
                        rowCount={20}
                        refreshTable={() => {}}
                        defaultColumnCells={alertColumnsConfig}
                        pagination={false}
                        pages={1}
                        activePage={0}
                      />
                    }
                    newEmptyContainer
                    loadingContent={<Loader />}
                    notFoundURL={`${envConfig.imageRoot}svg/not-found.svg`}
                  />
                </Content>
              )}
            </>
          )}
        </>
      ) : (
        <NumberCardContainer>
          <Badge color="#f87b0533" className="icon">
            {getEmoji(currentView)}
          </Badge>
          <Actions className="icon visibilityActions">
            <div onClick={deleteWidget.bind(this, widget)} className="ptr">
              <SVGIcon name="Delete" />
            </div>
          </Actions>
          {!viewPermission ? (
            <AccessDenied small />
          ) : (
            <>
              {!viewExist ? (
                <DeletedView small />
              ) : (
                <div className="content ptr" onClick={navigateToView}>
                  <NumberTitle>{listInfo?.totalCount}</NumberTitle>
                  {currentView?.name}
                </div>
              )}
            </>
          )}
        </NumberCardContainer>
      )}
      {/* Alert Occurrences Modal */}
      {modalProps.alertDetails && <AlertOccurrencesModal {...modalProps} />}
    </WidgetWrapper>
  );
}
export default withErrorBoundary(
  sizeMe({ monitorHeight: true })(AlertViewWidget)
);
